import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { platformAPI } from '../../../services/platformAPI';
import { activeImportChoices, keepCurrent, loadOrganisationSettings } from '../../../utils/organisationSettings';

const KINDS = [
  ['fee', 'tender_fee', 'Tender fee'],
  ['emd', 'emd', 'EMD']
];

const FALLBACK_PAYMENT_MODES = [
  { value: 'neft', label: 'NEFT' },
  { value: 'rtgs', label: 'RTGS' },
  { value: 'demand_draft', label: 'Demand draft' },
  { value: 'upi', label: 'UPI' },
  { value: 'bg', label: 'BG' },
  { value: 'online_portal', label: 'Online portal' }
];

const empty = (row) => ({
  applicability: row?.applicability || 'unknown',
  amount: row?.amount ?? '',
  currency: row?.currency || 'INR',
  payee: row?.payee || '',
  modes: (row?.modes || []).join(','),
  dueOn: row?.dueOn || '',
  validityRequiredUntil: row?.validityRequiredUntil || '',
  exemptionBasis: row?.exemptionBasis || '',
  physicalRequired: Boolean(row?.physicalRequired),
  physicalDueOn: row?.physicalDueOn || '',
  physicalAddress: row?.physicalAddress || '',
  onlineDueOn: row?.onlineDueOn || '',
  refundDueOn: row?.refundDueOn || ''
});

const FinanceCasePanel = ({ tenderId }) => {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [payments, setPayments] = useState({});
  const [paymentModes, setPaymentModes] = useState(FALLBACK_PAYMENT_MODES);

  const load = useCallback(() => platformAPI.getFinanceCase(tenderId).then((response) => {
    setData(response.data?.data || null);
  }).catch((error) => setMessage(error?.response?.data?.message || 'Fee and EMD details could not be loaded.')), [tenderId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    loadOrganisationSettings().then((pack) => {
      if (cancelled) return;
      const next = activeImportChoices(pack?.settings?.importLists?.paymentModes);
      setPaymentModes(next.length ? next : FALLBACK_PAYMENT_MODES);
    });
    return () => { cancelled = true; };
  }, []);

  const draft = (key, row) => drafts[key] || empty(row);
  const patch = (key, row, change) => setDrafts((current) => ({ ...current, [key]: { ...(current[key] || empty(row)), ...change } }));

  const save = async () => {
    setMessage('');
    const body = { reason };
    KINDS.forEach(([key]) => {
      const form = drafts[key];
      if (!form) return;
      body[key] = {
        ...form,
        amount: form.amount === '' ? null : Number(form.amount),
        modes: String(form.modes || '').split(',').map((item) => item.trim()).filter(Boolean)
      };
    });
    try {
      const response = await platformAPI.saveFinanceRequirements(tenderId, body);
      setData(response.data?.data || null);
      setDrafts({});
      setReason('');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'These requirements could not be saved.');
    }
  };

  const pay = async (kind, supersedesId) => {
    const draft = payments[kind] || {};
    if (!draft.reference || !draft.amount) {
      setMessage('Enter the reference and the amount. A blank amount is not invented.');
      return;
    }
    try {
      const response = await platformAPI.recordFinancePayment(tenderId, kind, {
        mode: draft.mode || 'neft',
        amount: Number(draft.amount),
        reference: draft.reference,
        paidOn: draft.paidOn || new Date().toISOString().slice(0, 10),
        supersedesId,
        reason: draft.reference
      });
      setData(response.data?.data || null);
      setPayments((current) => ({ ...current, [kind]: { mode: 'neft', reference: '', amount: '', paidOn: '' } }));
      setMessage('Payment recorded. The file, if any, is not verification.');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'The payment could not be recorded.');
    }
  };

  const verify = async (kind, paymentId) => {
    try {
      const response = await platformAPI.verifyFinance(tenderId, kind, paymentId ? { paymentId, decision: 'verified', comments: 'Evidence checked.' } : { decision: 'verified', comments: 'Exemption checked.' });
      setData(response.data?.data || null);
    } catch (error) {
      setMessage(error?.response?.data?.message || 'This evidence could not be verified.');
    }
  };

  if (!data) return message ? <Alert variant="warning">{message}</Alert> : null;

  return (
    <Alert variant="light" className="small mb-3">
      <strong>Tender fee and EMD</strong>
      <div className="mt-1">{data.progressNote}</div>
      <div className="text-muted">A tender fee is not the consultancy fee. Cash EMD is not a bank guarantee. A declaration is not a payment. Unknown, not applicable, exempt, and zero stay separate.</div>
      {message ? <div className="mt-2">{message}</div> : null}
      {KINDS.map(([key, kind, label]) => {
        const row = data[key];
        const form = draft(key, row);
        return (
          <div key={key} className="border rounded-0 p-2 mt-2 bg-white">
            <strong>{label}</strong>
            <span className="ms-2">{row.readiness}</span>
            {row.nonRefundable ? <span className="ms-2 text-muted">Normally non-refundable</span> : null}
            <div className="d-flex flex-wrap gap-2 mt-2">
              <Form.Select size="sm" value={form.applicability} onChange={(event) => patch(key, row, { applicability: event.target.value })}>
                <option value="unknown">Unknown</option>
                <option value="applicable">Applicable</option>
                <option value="not_applicable">Not applicable</option>
                <option value="exempt">Exempt / declaration</option>
                <option value="zero">Zero</option>
              </Form.Select>
              <Form.Control size="sm" placeholder="Amount, blank if unknown" value={form.amount} onChange={(event) => patch(key, row, { amount: event.target.value })} />
              <Form.Control size="sm" placeholder="Currency" value={form.currency} onChange={(event) => patch(key, row, { currency: event.target.value.toUpperCase() })} />
              <Form.Control size="sm" placeholder="Payee" value={form.payee} onChange={(event) => patch(key, row, { payee: event.target.value })} />
              <Form.Control size="sm" placeholder="Modes, comma separated" value={form.modes} onChange={(event) => patch(key, row, { modes: event.target.value })} />
              <Form.Control size="sm" type="date" value={form.dueOn} onChange={(event) => patch(key, row, { dueOn: event.target.value })} aria-label={`${label} due date`} />
              <Form.Control size="sm" type="date" value={form.onlineDueOn} onChange={(event) => patch(key, row, { onlineDueOn: event.target.value })} aria-label={`${label} online deadline`} />
              <Form.Control size="sm" type="date" value={form.physicalDueOn} onChange={(event) => patch(key, row, { physicalDueOn: event.target.value })} aria-label={`${label} physical deadline`} />
              <Form.Control size="sm" type="date" value={form.validityRequiredUntil} onChange={(event) => patch(key, row, { validityRequiredUntil: event.target.value })} aria-label={`${label} validity`} />
              {key === 'emd' ? <Form.Control size="sm" type="date" value={form.refundDueOn} onChange={(event) => patch(key, row, { refundDueOn: event.target.value })} aria-label="Refund due date" /> : null}
              <Form.Control size="sm" placeholder="Exemption basis" value={form.exemptionBasis} onChange={(event) => patch(key, row, { exemptionBasis: event.target.value })} />
            </div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <Form.Select size="sm" aria-label={`${label} payment mode`} value={(payments[kind] || {}).mode || paymentModes[0]?.value || 'neft'} onChange={(event) => setPayments((current) => ({ ...current, [kind]: { ...(current[kind] || {}), mode: event.target.value } }))}>
                {keepCurrent(paymentModes.map((row) => row.value), (payments[kind] || {}).mode, paymentModes.map((row) => row.value)).map((code) => {
                  const row = paymentModes.find((item) => item.value === code) || FALLBACK_PAYMENT_MODES.find((item) => item.value === code);
                  return <option key={code} value={code}>{row?.label || code}</option>;
                })}
              </Form.Select>
              <Form.Control size="sm" placeholder="Reference / UTR" aria-label={`${label} payment reference`} value={(payments[kind] || {}).reference || ''} onChange={(event) => setPayments((current) => ({ ...current, [kind]: { ...(current[kind] || {}), reference: event.target.value } }))} />
              <Form.Control size="sm" placeholder="Amount" aria-label={`${label} payment amount`} value={(payments[kind] || {}).amount || ''} onChange={(event) => setPayments((current) => ({ ...current, [kind]: { ...(current[kind] || {}), amount: event.target.value } }))} />
              <Form.Control size="sm" type="date" aria-label={`${label} payment date`} value={(payments[kind] || {}).paidOn || ''} onChange={(event) => setPayments((current) => ({ ...current, [kind]: { ...(current[kind] || {}), paidOn: event.target.value } }))} />
              <Button size="sm" variant="outline-secondary" onClick={() => pay(kind)}>Record payment</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => verify(kind)}>Verify exemption or zero</Button>
            </div>
            {(row.payments || []).map((payment) => (
              <div key={payment._id} className="mt-1">
                {payment.currency} {payment.amount} · {payment.reference || 'No reference'} · {payment.outcome} · {payment.verification}
                <Button size="sm" variant="link" onClick={() => verify(kind, payment._id)}>Verify</Button>
                {payment.outcome !== 'corrected' && payment.outcome !== 'failed' ? (
                  <Button size="sm" variant="link" onClick={() => pay(kind, payment._id)}>Correct with the reference above</Button>
                ) : null}
              </div>
            ))}
          </div>
        );
      })}
      <Form.Control className="mt-2" size="sm" placeholder="Reason for this change" value={reason} onChange={(event) => setReason(event.target.value)} />
      <Button className="mt-2" size="sm" onClick={save}>Save fee and EMD details</Button>
    </Alert>
  );
};

export default FinanceCasePanel;
